import { IKeyValue } from "motw_core/Entity/IKeyValue";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import * as React from "react";
import { GetDefaultKeyValue } from "source/app/infrastructure/Data/KeyValues/defaultKeyValue";
import { ActiveEmotionCharacter } from "source/app/presentation/BottomBar/EmotionSelector/EmotionCharacter/ActiveEmotion/ActiveEmotionCharacter";
import { IAppContainerState } from "source/app/presentation/AppContainer";
import { useIntervalToRerender } from "source/app/services/Shared/Hooks/useIntervalToRerender";
import { ShareState } from "source/app/services/UI/State/ShareState";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { State } from "source/app/services/UI/State/State";
import { Emotion } from "../../../domain/Emotions/Emotion";
import { Emotions } from "../../../domain/Emotions/Emotions";
import { IEmotion } from "../../../domain/Emotions/IEmotion";
import { Style } from "../../Shared/Styles/CSS";
import { InactiveEmotionCharacter } from "./EmotionCharacter/InactiveEmotionCharacter";
import { HandleEmotionSelectedLocallyAsync } from "./HandleEmotionSelected/HandleEmotionSelected";

export function EmotionSelector() {
    const defaultEmotionSelectorKeyValue = {
        ...GetDefaultKeyValue(),
        value: Emotions.Complacent.character,
    };

    const thirtyMinutesInMs = 30 * 60 * 1000;
    useIntervalToRerender(thirtyMinutesInMs);

    const [keyValue, setKeyValue] = React.useState<IKeyValue>(
        defaultEmotionSelectorKeyValue
    );
    const [isSavingEmotion, setIsSavingEmotion] = React.useState(false);
    const isSavingEmotionRef = React.useRef(false);

    ShareState(
        ESharedFunction.EmotionSelector_character,
        keyValue,
        setKeyValue
    );

    React.useEffect(() => {
        if (keyValue.id !== defaultEmotionSelectorKeyValue.id) {
            return;
        }

        let attempts = 0;
        const maxAttempts = 20;

        const loadPersistedEmotion = () => {
            const persistedEmotion = GetPersistedEmotionSelectorKeyValue();

            if (persistedEmotion) {
                setKeyValue(persistedEmotion);
                return true;
            }

            return false;
        };

        if (loadPersistedEmotion()) {
            return;
        }

        const intervalId = window.setInterval(() => {
            attempts++;

            if (loadPersistedEmotion() || attempts >= maxAttempts) {
                window.clearInterval(intervalId);
            }
        }, 250);

        return () => window.clearInterval(intervalId);
    }, [keyValue.id]);

    if (keyValue.id === defaultEmotionSelectorKeyValue.id) {
        return null;
    }

    const handleEmotionSelected = async (emotion: Emotion) => {
        if (isSavingEmotionRef.current) {
            return;
        }

        isSavingEmotionRef.current = true;
        setIsSavingEmotion(true);

        try {
            await HandleEmotionSelectedLocallyAsync(emotion);
        } finally {
            isSavingEmotionRef.current = false;
            setIsSavingEmotion(false);
        }
    };

    const emotionCharacter = keyValue.value;

    const containerStyle = Style(`emotionSelectorContainer`, {
        textAlign: "center",
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "center",
        alignContent: "stretch",
        alignItems: "flex-start",
        position: `relative`,
        top: `24px`,
    });

    const persistedEmotion = Emotions.GetFromCharacter(emotionCharacter);
    const activeEmotion =
        persistedEmotion === Emotions.IntegratedPlus
            ? Emotions.Integrated
            : persistedEmotion;
    const emotionsBeforeActive = Emotions.Before(activeEmotion).filter(
        (emotion) => emotion !== Emotions.IntegratedPlus
    );
    const emotionsAfterActive = Emotions.After(activeEmotion).filter(
        (emotion) => emotion !== Emotions.IntegratedPlus
    );

    const handleIntegratedPlusLongPress = () =>
        handleEmotionSelected(Emotions.IntegratedPlus);

    return (
        <div {...containerStyle}>
            {emotionsBeforeActive.map((e: Emotion, index: number) => (
                <InactiveEmotionCharacter
                    key={e.character}
                    emotion={e}
                    handleClick={() => handleEmotionSelected(e)}
                    handleLongPress={
                        e === Emotions.Integrated
                            ? handleIntegratedPlusLongPress
                            : undefined
                    }
                />
            ))}
            <ActiveEmotionCharacter
                emotion={activeEmotion}
                handleClick={() => handleEmotionSelected(activeEmotion)}
                handleLongPress={
                    activeEmotion === Emotions.Integrated
                        ? handleIntegratedPlusLongPress
                        : undefined
                }
                keyValue={keyValue}
                isSaving={isSavingEmotion}
            />
            {emotionsAfterActive.map((e: Emotion, index: number) => (
                <InactiveEmotionCharacter
                    key={e.character}
                    emotion={e}
                    handleClick={() => handleEmotionSelected(e)}
                    handleLongPress={
                        e === Emotions.Integrated
                            ? handleIntegratedPlusLongPress
                            : undefined
                    }
                />
            ))}
        </div>
    );
}

function GetPersistedEmotionSelectorKeyValue() {
    const appContainer = State.ReadClass<IAppContainerState>(
        ESharedComponent.AppContainer
    );

    return appContainer?.state.keyValues.find(
        (kv) => kv.key === ESharedFunction[ESharedFunction.EmotionSelector_character]
    );
}

export function EmotionIsAtLeast(
    sourceEmotion: IEmotion,
    emotionToCompareWith: IEmotion
) {
    if (!sourceEmotion || !emotionToCompareWith) {
        return false;
    }

    const result =
        Emotions.IndexOf(sourceEmotion) >=
        Emotions.IndexOf(emotionToCompareWith);
        
    return result;
}
